import { canWriteAtom, useCurrentPubkey } from '@/atoms/session'
import { addEventToCache, publishOpaquely } from '@/services/client.service'
import { getErrorMessage } from '@/lib/error'
import { HugeiconsIcon } from '@hugeicons/react'
import { AddCircleIcon, CheckIcon, UserAdd01Icon } from '@hugeicons/core-free-icons'
import { invalidateFavoriteFollowSets } from '@/atoms/invalidation'
import { useTranslation } from 'react-i18next'
import { unwrappedFavoriteFollowListsWithSetsAtom } from '@/atoms/lists'
import { toast } from 'sonner'
import { now } from '@/lib/utils'
import * as kinds from '@nostr/tools/kinds'
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import { useAtomValue } from 'jotai'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { loadFavoriteFollowSets } from '@nostr/gadgets/lists'
import { getReplaceableCoordinateFromEvent } from '@/lib/event'

export default function SaveToFollowSetItems({ pubkey }: { pubkey: string }) {
  const { t } = useTranslation()
  const accountPubkey = useCurrentPubkey()
  const canWrite = useAtomValue(canWriteAtom)
  const favoriteFollowSets = useAtomValue(unwrappedFavoriteFollowListsWithSetsAtom)

  if (!canWrite || !accountPubkey) return null

  return (
    <>
      <DropdownMenuItem disabled>{t('Add to follow set')}</DropdownMenuItem>
      {favoriteFollowSets?.length === 0 ? (
        <DropdownMenuItem disabled>{t('No follow sets yet')}</DropdownMenuItem>
      ) : (
        favoriteFollowSets?.map((set) => (
          <FollowSetItem
            key={set.pointer.pubkey + set.pointer.identifier}
            set={set}
            pubkey={pubkey}
          />
        ))
      )}
      <DropdownMenuSeparator />
      <SaveToNewFollowSet pubkey={pubkey} />
    </>
  )
}

function FollowSetItem({ set, pubkey }: { set: ResolvedSet<string>; pubkey: string }) {
  const { t } = useTranslation()
  const accountPubkey = useCurrentPubkey()
  const saved = set.items.includes(pubkey)

  return (
    <DropdownMenuItem className="flex gap-2" onClick={handleClick}>
      {saved ? <HugeiconsIcon icon={CheckIcon} /> : <HugeiconsIcon icon={AddCircleIcon} />}
      {set.title}
    </DropdownMenuItem>
  )

  async function handleClick() {
    if (!accountPubkey) return

    const updatedPubkeys = saved ? set.items.filter((p) => p !== pubkey) : [...set.items, pubkey]
    const event = await publishOpaquely({
      kind: kinds.Followsets,
      content: '',
      tags: [
        ['d', set.pointer.identifier],
        ...(set.title && set.title !== set.pointer.identifier ? [['title', set.title]] : []),
        ...updatedPubkeys.map((p) => ['p', p])
      ],
      created_at: now()
    }).catch((err) => {
      console.warn('failed to publish updated follow set event:', getErrorMessage(err))
      return null
    })
    if (!event) {
      toast.error(t('Failed to publish updated follow set event.'))
      return
    }

    addEventToCache(event)
    invalidateFavoriteFollowSets()
  }
}

function SaveToNewFollowSet({ pubkey }: { pubkey: string }) {
  const { t } = useTranslation()
  const accountPubkey = useCurrentPubkey()

  return (
    <DropdownMenuItem onClick={handleSave}>
      <HugeiconsIcon icon={UserAdd01Icon} />
      {t('Save to a new follow set')}
    </DropdownMenuItem>
  )

  async function handleSave() {
    if (!accountPubkey) return

    const newSetName = prompt(t('Enter a name for the new follow set'))
    if (!newSetName) return

    const newId = Date.now().toString(36)
    const setEvent = await publishOpaquely({
      kind: kinds.Followsets,
      content: '',
      tags: [
        ['d', newId],
        ['title', newSetName],
        ['p', pubkey]
      ],
      created_at: now()
    }).catch((err) => {
      console.warn('failed to publish follow set event:', getErrorMessage(err))
      return null
    })
    if (!setEvent) {
      toast.error(t('Failed to publish follow set event.'))
      return
    }

    addEventToCache(setEvent)

    const fr = await loadFavoriteFollowSets(accountPubkey, [], true)
    const coordinate = getReplaceableCoordinateFromEvent(setEvent)
    const tags = fr.event
      ? [...fr.event.tags.filter((t) => !(t[0] === 'a' && t[1] === coordinate)), ['a', coordinate]]
      : [['a', coordinate]]

    const favEvent = await publishOpaquely({
      kind: 10021,
      content: '',
      tags,
      created_at: now()
    }).catch((err) => {
      console.warn('failed to publish favorite follow sets event:', getErrorMessage(err))
      return null
    })
    if (!favEvent) {
      toast.error(t('Failed to publish favorite follow sets event.'))
      return
    }

    await loadFavoriteFollowSets(accountPubkey, undefined, favEvent)
    invalidateFavoriteFollowSets()
  }
}
